/**
 * SelectionManager - Utility module for managing file/folder selection state
 * 
 * Provides selection logic for the FileExplorer component including:
 * - Single click selection
 * - Ctrl/Cmd+click toggle selection
 * - Shift+click range selection
 * - Select All functionality
 * 
 * @module SelectionManager
 * 
 * Requirements: 8.1, 8.2, 8.3
 * - 8.1: Allow selecting multiple files via checkbox or Ctrl/Cmd+click
 * - 8.2: Support Shift+click to select a range of files
 * - 8.3: Provide a "Select All" option for the current folder
 */

/**
 * Creates a new selection state
 * @returns {Set<string>} Empty Set for tracking selected item keys
 */
export function createSelectionState() {
  return new Set();
}

/**
 * Handles single click selection - selects only the clicked item
 * Clears any existing selection and selects only the clicked item.
 * 
 * @param {string} itemKey - The key of the clicked item
 * @param {Set<string>} currentSelection - Current selection state
 * @returns {Set<string>} New selection state containing only the clicked item
 * 
 * @example
 * const newSelection = handleSingleClick('file1.txt', currentSelection);
 * // newSelection contains only 'file1.txt'
 */
export function handleSingleClick(itemKey, currentSelection) {
  // Return empty set if no item key provided
  if (!itemKey) {
    return new Set();
  }
  
  // Create new set with only the clicked item
  return new Set([itemKey]);
}

/**
 * Handles Ctrl/Cmd+click toggle selection
 * Toggles the selection state of the clicked item without affecting other selections.
 * 
 * Property 11: Toggle Selection (Ctrl+Click)
 * For any item clicked with Ctrl/Cmd held, if the item was not selected it shall be 
 * added to the selection, if it was selected it shall be removed, and all other 
 * selections shall remain unchanged.
 * 
 * @param {string} itemKey - The key of the clicked item
 * @param {Set<string>} currentSelection - Current selection state
 * @returns {Set<string>} New selection state with the item toggled
 * 
 * @example
 * // Item not selected - adds it
 * const newSelection = handleCtrlClick('file1.txt', new Set(['file2.txt']));
 * // newSelection contains 'file1.txt' and 'file2.txt'
 * 
 * // Item already selected - removes it
 * const newSelection = handleCtrlClick('file1.txt', new Set(['file1.txt', 'file2.txt']));
 * // newSelection contains only 'file2.txt'
 */
export function handleCtrlClick(itemKey, currentSelection) {
  // Return current selection if no item key provided
  if (!itemKey) {
    return new Set(currentSelection);
  }
  
  // Create a new Set from current selection
  const newSelection = new Set(currentSelection);
  
  // Toggle the item
  if (newSelection.has(itemKey)) {
    newSelection.delete(itemKey);
  } else {
    newSelection.add(itemKey);
  }
  
  return newSelection;
}

/**
 * Handles Shift+click range selection
 * Selects all items between the last selected item and the clicked item (inclusive),
 * based on the current sort order of items.
 * 
 * Property 12: Range Selection (Shift+Click)
 * For any Shift+click on an item, all items between the last selected item and the 
 * clicked item (inclusive) shall be selected, based on the current sort order.
 * 
 * @param {string} itemKey - The key of the clicked item
 * @param {Array<Object|string>} items - Array of all items in current view (in display order)
 * @param {string|null} lastSelectedKey - The key of the last selected item (anchor point)
 * @param {Set<string>} currentSelection - Current selection state
 * @returns {Set<string>} New selection state with the range selected
 * 
 * @example
 * const items = [{ key: 'a' }, { key: 'b' }, { key: 'c' }, { key: 'd' }];
 * const newSelection = handleShiftClick('c', items, 'a', new Set(['a']));
 * // newSelection contains 'a', 'b', 'c'
 */
export function handleShiftClick(itemKey, items, lastSelectedKey, currentSelection) {
  // If no item key provided, return current selection
  if (!itemKey) {
    return new Set(currentSelection);
  }
  
  // If no items array or empty, just select the clicked item
  if (!items || !Array.isArray(items) || items.length === 0) {
    return new Set([itemKey]);
  }
  
  // If no last selected item, treat as single click
  if (!lastSelectedKey) {
    return new Set([itemKey]);
  }
  
  // Get the keys from items array
  const itemKeys = items.map(item => {
    if (typeof item === 'string') {
      return item;
    }
    return item.key || item.name || '';
  });
  
  // Find indices of the anchor (last selected) and the clicked item
  const anchorIndex = itemKeys.indexOf(lastSelectedKey);
  const clickedIndex = itemKeys.indexOf(itemKey);
  
  // If either item is not found in the list, just select the clicked item
  if (anchorIndex === -1 || clickedIndex === -1) {
    return new Set([itemKey]);
  }
  
  // Determine the range (start to end, inclusive)
  const startIndex = Math.min(anchorIndex, clickedIndex);
  const endIndex = Math.max(anchorIndex, clickedIndex);
  
  // Create new selection with all items in the range
  const newSelection = new Set(currentSelection);
  
  for (let i = startIndex; i <= endIndex; i++) {
    if (itemKeys[i]) {
      newSelection.add(itemKeys[i]);
    }
  }
  
  return newSelection;
}

/**
 * Selects all items in the current view
 * 
 * Property 13: Select All
 * For any folder, when "Select All" is triggered, the selection set shall contain 
 * exactly all files and folders in the current view.
 * 
 * @param {Array<Object|string>} items - Array of all items to select
 * @returns {Set<string>} New selection state containing all item keys
 * 
 * @example
 * const items = [{ key: 'file1.txt' }, { key: 'file2.txt' }];
 * const newSelection = selectAll(items);
 * // newSelection contains 'file1.txt' and 'file2.txt'
 */
export function selectAll(items) {
  // Return empty set if no items
  if (!items || !Array.isArray(items) || items.length === 0) {
    return new Set();
  }
  
  // Extract keys from all items
  const keys = items.map(item => {
    if (typeof item === 'string') {
      return item;
    }
    return item.key || item.name || '';
  }).filter(key => key !== '');
  
  return new Set(keys);
}

/**
 * Clears all selections
 * 
 * @returns {Set<string>} Empty selection state
 * 
 * @example
 * const newSelection = clearSelection();
 * // newSelection is an empty Set
 */
export function clearSelection() {
  return new Set();
}

/**
 * Checks if an item is selected
 * 
 * @param {string} itemKey - The key of the item to check
 * @param {Set<string>} selection - Current selection state
 * @returns {boolean} True if the item is selected
 * 
 * @example
 * const selection = new Set(['file1.txt', 'file2.txt']);
 * isSelected('file1.txt', selection); // true
 * isSelected('file3.txt', selection); // false
 */
export function isSelected(itemKey, selection) {
  if (!itemKey || !selection) {
    return false;
  }
  return selection.has(itemKey);
}

/**
 * Gets the count of selected items
 * 
 * @param {Set<string>} selection - Current selection state
 * @returns {number} Number of selected items
 * 
 * @example
 * const selection = new Set(['file1.txt', 'file2.txt']);
 * getSelectionCount(selection); // 2
 */
export function getSelectionCount(selection) {
  if (!selection) {
    return 0;
  }
  return selection.size;
}

/**
 * Gets an array of selected item keys
 * 
 * @param {Set<string>} selection - Current selection state
 * @returns {Array<string>} Array of selected item keys
 * 
 * @example
 * const selection = new Set(['file1.txt', 'file2.txt']);
 * getSelectedItems(selection); // ['file1.txt', 'file2.txt']
 */
export function getSelectedItems(selection) {
  if (!selection) {
    return [];
  }
  return Array.from(selection);
}

/**
 * Determines the selection action based on keyboard modifiers
 * 
 * @param {Object} event - The click event
 * @param {boolean} event.ctrlKey - Whether Ctrl key is pressed
 * @param {boolean} event.metaKey - Whether Meta/Cmd key is pressed
 * @param {boolean} event.shiftKey - Whether Shift key is pressed
 * @returns {'single'|'toggle'|'range'} The selection mode to use
 * 
 * @example
 * getSelectionMode({ ctrlKey: true, metaKey: false, shiftKey: false }); // 'toggle'
 * getSelectionMode({ ctrlKey: false, metaKey: false, shiftKey: true }); // 'range'
 * getSelectionMode({ ctrlKey: false, metaKey: false, shiftKey: false }); // 'single'
 */
export function getSelectionMode(event) {
  if (!event) {
    return 'single';
  }
  
  // Shift+click for range selection
  if (event.shiftKey) {
    return 'range';
  }
  
  // Ctrl+click (Windows/Linux) or Cmd+click (Mac) for toggle selection
  if (event.ctrlKey || event.metaKey) {
    return 'toggle';
  }
  
  // Default to single selection
  return 'single';
}

/**
 * Handles a click event and returns the new selection state
 * This is a convenience function that combines getSelectionMode with the appropriate handler.
 * 
 * @param {string} itemKey - The key of the clicked item
 * @param {Object} event - The click event with modifier keys
 * @param {Array<Object|string>} items - Array of all items in current view
 * @param {string|null} lastSelectedKey - The key of the last selected item
 * @param {Set<string>} currentSelection - Current selection state
 * @returns {Object} Object containing new selection and the clicked item key (for tracking last selected)
 * @property {Set<string>} selection - The new selection state
 * @property {string} lastSelected - The key to use as the new lastSelectedKey
 * 
 * @example
 * const result = handleClick('file1.txt', event, items, lastSelected, currentSelection);
 * // result.selection is the new Set
 * // result.lastSelected is 'file1.txt'
 */
export function handleClick(itemKey, event, items, lastSelectedKey, currentSelection) {
  const mode = getSelectionMode(event);
  
  let newSelection;
  
  switch (mode) {
    case 'toggle':
      newSelection = handleCtrlClick(itemKey, currentSelection);
      break;
    case 'range':
      newSelection = handleShiftClick(itemKey, items, lastSelectedKey, currentSelection);
      break;
    case 'single':
    default:
      newSelection = handleSingleClick(itemKey, currentSelection);
      break;
  }
  
  return {
    selection: newSelection,
    lastSelected: itemKey
  };
}
